import React, { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Tabs, Tab, TabList, TabPanel } from 'react-tabs'
import 'react-tabs/style/react-tabs.css'
import { InboxType } from '../../../server/models/Inbox'

// Components
import { Page, Section } from '../../components/Page'
import { Skeleton } from '../../components/Skeleton'
import { Empty } from '../../components/Empty'
import { InboxContent } from '../../components/InboxContent'

// Handlers
import { handleError } from '../../errors/handleError'

// Context
import { useMyInboxes } from '../../contexts/MyInboxesContext'

import './styles.css'

export const Content: React.FC = () => {
  const { inboxMemberships, loading } = useMyInboxes()
  const [inboxes, setInboxes] = useState<InboxType[]>([])
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const fetchInboxes = async () => {
      try {
        // Filter only membership objects that have a populated inbox
        const loadedInboxes = inboxMemberships
          .filter(membership => membership.inbox)
          .map(membership => membership.inbox as InboxType)
                
        setInboxes(loadedInboxes)

        // Set default inbox in URL if none exists and we have inboxes
        const currentInboxParam = searchParams.get('inbox')
        if (loadedInboxes.length > 0 && !currentInboxParam) {
          setSearchParams({ inbox: loadedInboxes[0]._id || '' })
        }
      } catch (error) {
        handleError(error, 'Failed to fetch your inboxes. Please try again later.')
      }
    }

    if (inboxMemberships.length > 0) {
      fetchInboxes()
    }
  }, [inboxMemberships, setSearchParams])

  // Get selected inbox ID from URL
  const selectedInboxId = searchParams.get('inbox')
    
  // Find the index of the selected inbox
  const selectedIndex = useMemo(() => {
    if (!selectedInboxId || inboxes.length === 0) {
      return 0
    }
    const index = inboxes.findIndex(inbox => inbox._id === selectedInboxId)
    return index >= 0 ? index : 0
  }, [selectedInboxId, inboxes])

  // Handle tab change
  const handleTabChange = (index: number) => {
    if (inboxes[index]) {
      setSearchParams({ inbox: inboxes[index]._id || '' })
    }
  }

  if (loading) {
    return (
      <Page>
        <Section title="Content"> </Section>
        <Tabs>
          <TabList>
            <Tab>
              <Skeleton width={80} />
            </Tab>
            <Tab>
              <Skeleton width={120} />
            </Tab>
            <Tab>
              <Skeleton width={100} />
            </Tab>
            <Tab>
              <Skeleton width={60} />
            </Tab>
          </TabList>
          <TabPanel>
            <Empty>
                Brewing up the coffee...
            </Empty>
          </TabPanel>
          <TabPanel>
            <Empty>
                Brewing up the coffee...
            </Empty>
          </TabPanel>
          <TabPanel>
            <Empty>
                Brewing up the coffee...
            </Empty>
          </TabPanel>
          <TabPanel>
            <Empty>
                Brewing up the coffee...
            </Empty>
          </TabPanel>
        </Tabs>
      </Page>
    )
  }

  return (
    <Page>
      <Section title="Content"> </Section>
      {inboxes.length === 0 && (
        <Empty>
                    You have no inboxes to show.
        </Empty>
      )}
      {inboxes.length > 0 && (
        <Tabs 
          forceRenderTabPanel
          selectedIndex={selectedIndex}
          onSelect={handleTabChange}
        >
          <TabList>
            {inboxes.map(inbox => (
              <Tab key={inbox._id}>
                {inbox.name}
              </Tab>
            ))}
          </TabList>
          {inboxes.map(inbox => (
            <TabPanel key={inbox._id}>
              <InboxContent inboxId={inbox._id} />
            </TabPanel>
          ))}
        </Tabs>
      )}
    </Page>
  )
}
